import React, { useState } from 'react';
import $ from 'jquery';
import { Button, Modal, ModalHeader, ModalBody, Alert, Card, CardHeader, CardBody } from 'reactstrap';
import DataStore from '../base/plumbing/DataStore';
import PropControl from '../base/components/PropControl';
import { Card as CardCollapsable } from "../base/components/CardAccordion";
import { ellipsize, space, stopEvent } from '../base/utils/miscutils';
import Misc from './MiscOverrides';
import Login from '../base/youagain';
import { saveTag } from './greentag/greenTagPageUtils';
import Icon from '../base/components/Icon';

import '../../style/Notes.less';
import { getId } from '../base/data/DataClass';


const NOTE_WIDGET_PATH = ["widget", "Notes"];


const urlIsImage = (url) => {
	const imgFiletypes = ["png", "jpeg", "jpg", "gif", "webp", "svg"];
	let isImg = false;
	imgFiletypes.forEach(filetype => {
		const regex = new RegExp('\\.' + filetype + '$');
		if (url.toLowerCase().match(regex)) {
			isImg = true;
		}
	});
	return isImg;
};


const urlFiletype = (url) => {
	const regex = /.*\/.*\.(.*)$/g;
	const match = regex.exec(url);
	if (match && match.length) {
		return match[1];
	}
	return null;
};


const urlFilename = (url) => {
	const regex = /.*\/(.*\..*)$/g;
	const match = regex.exec(url);
	if (match && match.length) {
		return match[1];
	}
	return null;
};


function XButton({onClick, ...props}) {
	return <Button className="x-button" size="xs" color="danger" onClick={onClick} {...props} >
		<Icon name="x" />
	</Button>;
}


/**
 * Add a new note, works with any object with a "notes" field
 * @param {Object} p
 * @param {DataItem} item
 */
function AddNoteButton({ item, color = 'secondary', onClick, children, ...props }) {
	if (onClick) console.error("AddNoteButton", "Overriding onClick will prevent component functionality. Use a normal button!");

	const newNote = () => {
		const notesPath = DataStore.getDataPath(item).concat('notes');
		const currentNotes = DataStore.getValue(notesPath) || [];
		// Construct an empty note & add to item.notes (TODO Let's have Note.js and a constructor)
		const dateTime = new Date().toUTCString();
		const author = Login.getUser().name;
		currentNotes.push({ text: '', attachments: [], dateTime, author, important: false });
		DataStore.setValue(notesPath, currentNotes);
		// Open the editor
		const nextWidgetProps = { ...DataStore.getValue(NOTE_WIDGET_PATH), edit: true, open: true, noteIdx: (currentNotes.length - 1) };
		DataStore.setValue(NOTE_WIDGET_PATH, nextWidgetProps);
	};

	return <Button color={color} onClick={newNote} {...props}>{children || "Add note"}</Button>;
}


/**
 * Edit or view a note
 * Most of the props for this modal are done through DataStore, to make this an Uncontrolled component, under NOTE_WIDGET_PATH
 * - open: boolean, opens the modal
 * - edit: boolean, view the note in editing or viewing only mode
 * - noteIdx: number, the index of the note to view
 * @param {Object} p
 * @param {DataItem} [p.item]
 */
function EditNoteModal({ item }) {
	if (!item) return null;
	const [aCollapse, setACollapse] = useState(true);
	const toggleACollapse = () => setACollapse(!aCollapse);
	const [loading, setLoading] = useState(false);
	const [warning, setWarning] = useState(null);
	const [attachType, setAttachType] = useState(null);

	const { open: isOpen, edit } = DataStore.getValue(NOTE_WIDGET_PATH) || {};

	// Open/close the editor
	const toggleOpen = () => {
		if (edit && isOpen) saveTag(item); // Save on close
		DataStore.setValue(NOTE_WIDGET_PATH.concat('open'), !isOpen);
	};

	const noteIdx = DataStore.getValue(NOTE_WIDGET_PATH.concat("noteIdx"));
	const path = DataStore.getDataPath(item).concat(["notes", noteIdx]);

	const note = (noteIdx || noteIdx === 0) ? DataStore.getValue(path) : null;
	let content;
	if (!note) {
		content = <Misc.Loading />;
	} else {
		const attachments = DataStore.getValue(path.concat("attachments")) || [];

		const attachImage = () => {
			setWarning(false);
			const imgUrl = DataStore.getValue(NOTE_WIDGET_PATH.concat("img"));
			if (!urlIsImage(imgUrl)) {
				setWarning("Unsupported image type! Please use png, jpg, gif, webp or svg");
				return;
			}
			if (imgUrl && imgUrl !== "") {
				setLoading(true);
				// Load the image manually to check it's valid
				const imgTest = new Image();
				imgTest.onload = () => {
					attachments.push(imgUrl);
					DataStore.setValue(path.concat("attachments"), attachments);
					// clear input
					DataStore.setValue(NOTE_WIDGET_PATH.concat("img"), "");
					setWarning(null);
					setLoading(false);
				};
				imgTest.onerror = () => {
					setWarning("Failed to load image - if using a URL, check it's correct!");
					setLoading(false);
				};
				imgTest.src = imgUrl;
			}
		};

		const attachFile = () => {
			setWarning(false);
			const fileUrl = DataStore.getValue(NOTE_WIDGET_PATH.concat("file"));
			if (fileUrl && fileUrl !== "") {
				setLoading(true);
				const settings = {
					cache: false,
					async: true,
					crossDomain: true,
					url: fileUrl,
					method: "GET",
					headers: { 'Access-Control-Allow-Origin': '*' },
					success: (data, status) => {
						attachments.push(fileUrl);
						DataStore.setValue(path.concat("attachments"), attachments);
						// clear input
						DataStore.setValue(NOTE_WIDGET_PATH.concat("file"), "");
						setWarning(null);
						setLoading(false);
					},
					error: (xhr, status, error) => {
						setWarning("Failed to load file - if using a URL, check it's correct!");
						setLoading(false);
					}
				};
				// Make an AJAX call to confirm the URL loads
				$.ajax(settings);
			}
		};

		const removeAttachment = i => {
				const nextAttachments = attachments.toSpliced(i, 1);
				DataStore.setValue(path.concat('attachments'), nextAttachments);
		};

		let attachContent = null;
		if (attachType === "image") {
			attachContent = <>
				<PropControl type="imgUpload" prop="img" path={NOTE_WIDGET_PATH} />
				<Button type="secondary" disabled={loading} onClick={attachImage}>Attach</Button>
			</>;
		} else if (attachType === "file") {
			attachContent = <>
				<PropControl type="upload" prop="file" path={NOTE_WIDGET_PATH} />
				<Button type="secondary" disabled={loading} onClick={attachFile}>Attach</Button>
			</>;
		}

		content = <>
			<PropControl type="textarea" style={{ height: "20rem" }} prop="text" path={path} disabled={!edit} />
			{edit ? (
				<PropControl type="checkbox" label="Mark as important" prop="important" path={path} />
			) : (
				note && note.important && <b>Important</b>
			)}
			{(edit || attachments.length > 0) && <CardCollapsable title={`Attachments (${attachments.length})`} collapse={aCollapse} onHeaderClick={toggleACollapse}>
				{(attachments.length > 0 || loading) && <>
					<div style={{ overflowX: "auto" }} className="d-flex flex-row justify-content-start align-items-center">
						{attachments.map((url, i) => <div key={url} className="attachment mr-2 position-relative overflow-hidden" style={{ height: 100, minWidth: 150 }}>
							<img src={urlIsImage(url) ? url : "/img/blank-file.png"} style={{ maxHeight: 100 }} />
							<div className="position-absolute text-center d-flex flex-column justify-content-center align-items-center text-center"
								style={{ top: "50%", left: "50%", transform: "translate(-50%,-50%)" }}
							>
								<a href={url} target="_blank" download>
									<p className="p-1 rounded bg-gl-darker-grey text-white" style={{ maxWidth: 120 }}>
										{ellipsize(urlFilename(url), 10)}
									</p>
								</a>
							</div>
							{edit && <XButton onClick={() => removeAttachment(i)} />}
						</div>)}
						{loading && <div className="attachment mr-2 position-relative" style={{ height: 100 }}>
							<b>Loading...</b>
						</div>}
					</div>
					<hr />
				</>}
				{edit && <>
					<div className="d-flex flex-row justify-content-center">
						<Button color="secondary" style={{ width: "5rem" }} onClick={() => setAttachType("image")}>+ Image</Button>
						&nbsp;
						<Button color="secondary" style={{ width: "5rem" }} onClick={() => setAttachType("file")}>+ File</Button>
					</div>
					{attachContent && <>
						<br />
						{attachContent}
					</>}
					{warning && <><br /><br /><Alert color="danger">{warning}</Alert></>}
				</>}
			</CardCollapsable>}
			<Button type="primary" onClick={toggleOpen}>Done</Button>
		</>;
	}

	return <Modal isOpen={isOpen} toggle={toggleOpen} style={{ maxWidth: 700 }} className="note-modal">
		<ModalHeader style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>
			{edit ? 'Editing note' : `Viewing note${note ? ` from ${note.author}` : ''}`}
			<br/>
			<small>{note && note.dateTime}</small>
		</ModalHeader>
		<ModalBody>
			{content}
		</ModalBody>
	</Modal>;
}


/**
 * Read and manage notes on a data-item.
 * Built for Green Ad Tags, but should work for anything with an array of notes at item.notes.
 * @param {Object} p
 * @param {DataItem} [p.item]
 */
function NotesList({item, className, ...props}) {
	if (!item) return null;
	const path = DataStore.getDataPath(item);
	const notesPath = path.concat('notes');

	const notes = item.notes || [];

	const removeNote = (e, i) => {
		stopEvent(e);
		notes.splice(i, 1);
		DataStore.setValue(notesPath, notes);
		saveTag(item);
	};

	const openNote = (i) => {
		DataStore.setValue(NOTE_WIDGET_PATH.concat('noteIdx'), i);
		DataStore.setValue(NOTE_WIDGET_PATH.concat('edit'), false);
		DataStore.setValue(NOTE_WIDGET_PATH.concat('open'), true);
	};

	return <div className={space('notes-list', className)} {...props}>
		{notes.map((note, i) => {
			const { dateTime, author, text, important } = note;
			const firstLine = text.trim().split('\n', 1)[0];
			return <Card key={`note-${getId(item)}-${i}`} className={space('note mb-1', important && 'important')} onClick={() => openNote(i)}>
				<CardHeader>
					<span className="note-author">{author}</span> at <span className="note-timestamp">{dateTime}</span>
					<XButton onClick={(e) => removeNote(e, i)} title="Delete this note" />
				</CardHeader>
				<CardBody className="note-text" title={firstLine}>
					{firstLine}
				</CardBody>
		</Card>;
		})}
	</div>;
}


const noteCount = (item) => {
	return item?.notes?.length || 0;
};


export { EditNoteModal, AddNoteButton, NotesList, noteCount };
